import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { HeroTitle } from '@/components/generals/hero-title'
import { SectionLabel } from '@/components/generals/section-label'

export interface LegalSection {
    heading: string
    paragraphs: string[]
}

interface LegalPageProps {
    label: string
    title: string
    updated: string
    sections: LegalSection[]
}

const LegalPage: FC<LegalPageProps> = ({ label, title, updated, sections }): ReactNode => {
    return (
        <>
            <section aria-labelledby='legal-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 pb-12 pt-32 md:px-10 md:pb-16 md:pt-40'>
                    <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-5'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/20' style={revealDelay(100)} />
                        <SectionLabel className='reveal-fade'>{label}</SectionLabel>
                        <p className='reveal-fade font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/80' style={revealDelay(150)}>Last updated {updated}</p>
                    </div>
                    <div className='mt-10 grid gap-8 md:mt-14 lg:grid-cols-12 lg:items-end'>
                        <HeroTitle id='legal-title' className='type-hero lg:col-span-8' delay={200} lines={[title]} />
                        <p className='reveal-fade max-w-sm text-[15px] leading-relaxed text-charcoal/85 lg:col-span-4' style={revealDelay(380)}>
                            Questions about this page? <Link href='/contact' className='font-semibold underline underline-offset-4 hover:text-charcoal/70'>Get in touch</Link> and we will reply within one business day.
                        </p>
                    </div>
                </div>
            </section>

            <section aria-label={title} data-nav-theme='light' className='bg-cream text-charcoal'>
                <div className='mx-auto grid max-w-[1440px] gap-12 border-t border-charcoal/20 px-5 py-16 md:px-10 md:py-24 lg:grid-cols-12 lg:gap-10'>
                    <nav aria-label='On this page' className='lg:col-span-3'>
                        <ol className='space-y-3 lg:sticky lg:top-28'>
                            {sections.map((section, index) => (
                                <li key={section.heading}>
                                    <a href={`#section-${index + 1}`} className='flex gap-3 text-[14px] text-charcoal/85 hover:text-charcoal hover:underline'>
                                        <span className='font-mono text-xs text-charcoal/70'>{pad(index + 1)}</span>
                                        {section.heading}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    <div className='max-w-[68ch] space-y-12 lg:col-span-8 lg:col-start-5'>
                        {sections.map((section, index) => (
                            <div key={section.heading} id={`section-${index + 1}`} className='scroll-mt-28'>
                                <h2 className='font-heading text-2xl font-extrabold tracking-[-0.025em]'>
                                    <span className='mr-3 font-mono text-sm font-normal text-charcoal/70'>{pad(index + 1)}</span>
                                    {section.heading}
                                </h2>
                                <div className='mt-4 space-y-4 text-[16px] leading-[1.75] text-charcoal/90'>
                                    {section.paragraphs.map((paragraph) => (
                                        <p key={paragraph}>{paragraph}</p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    )
}

export default LegalPage
